import {
  putUsersPreferencesWaterCharacteristics,
  useGetUsersPreferencesWaterCharacteristicsSuspense,
} from "@imi/api-client/index.js";
import {
  getWaterCharacteristicsSchema,
  WaterCharacteristicsPreference,
} from "@src/core/api/waterCharacteristicsPreferences";
import { extractDefaultValues } from "@src/core/engine/helpers/extractDefaultValues";
import { waterCharacteristicsRepository } from "@src/core/engine/technicalParameters/waterCharacteristics/waterCharacteristicsRepository";
import { useToast } from "@src/shared/components/Toast/useToast";
import { useAppForm } from "@src/shared/helpers/form";
import { logger } from "@src/shared/helpers/logger";
import { mergeWithDefaults } from "@src/shared/helpers/mergeWithDefaults";
import { parseApiError } from "@src/shared/helpers/parseApiError";
import { setUnchangedToNull } from "@src/shared/helpers/setUnchangedToNull";
import { revalidateLogic } from "@tanstack/react-form";
import { set, tryit } from "radashi";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type UseWaterCharacteristicsFormOptions = {
  onSubmit?: () => void;
};

export function useWaterCharacteristicsForm(
  options?: UseWaterCharacteristicsFormOptions,
) {
  const [submitting, setSubmitting] = useState(false);
  const { data } = useGetUsersPreferencesWaterCharacteristicsSuspense();
  const { addToast } = useToast();
  const { t } = useTranslation("settings", {
    keyPrefix: "technicalPreferences.waterCharacteristics",
  });

  const definitions = waterCharacteristicsRepository.getDefinitions();
  const defaultValue = useMemo(
    () => extractDefaultValues(definitions) as WaterCharacteristicsPreference,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const formDefaultValue = useMemo(
    () => mergeWithDefaults(data ?? {}, defaultValue),
    [data, defaultValue],
  ) as WaterCharacteristicsPreference;

  const form = useAppForm({
    defaultValues: formDefaultValue,
    async onSubmit({ value }) {
      setSubmitting(true);
      const body = setUnchangedToNull(value, defaultValue, {
        ignoreKeys: ["mode"],
      });
      const [error] = await tryit(putUsersPreferencesWaterCharacteristics)({
        body:
          body.mode === "manual"
            ? body
            : set(body, "weight", body.additives === null ? null : body.weight),
      });
      setSubmitting(false);

      if (error) {
        const apiError = parseApiError({ error });
        logger.error("Failed to update water characteristics preference", {
          apiError,
          error,
        });
        return;
      }

      addToast({
        duration: 3000,
        message: t("update.success.description"),
        title: t("update.success.title"),
      });
      options?.onSubmit?.();
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: {
      onDynamic: ({ value }) => {
        const waterCharacteristics =
          value?.mode === "predefined" ? value : undefined;
        const definitions =
          waterCharacteristicsRepository.getDefinitions(waterCharacteristics);
        const validation = getWaterCharacteristicsSchema({
          definitions,
        }).safeParse(value);
        if (validation.success) return undefined;
        const fields: Record<string, string> = {};
        for (const issue of validation.error.issues) {
          const path = issue.path.join(".");
          if (path) {
            fields[path] = issue.message;
          }
        }
        return { fields };
      },
    },
  });

  return {
    defaultValue,
    definitions,
    form,
    submitting,
  };
}
